import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, TooltipProps } from '@mui/material';
import {
	getFullscreenPopperSlotProps,
	useFullscreenContainer,
} from '../../utils/fullscreenContainer';
interface TruncatedTooltipProps {
	text: string;
	className?: string;
	placement?: TooltipProps['placement'];
}

/**
 * Shows a tooltip with the full text only when the content is visually truncated.
 */
const TruncatedTooltip: React.FC<TruncatedTooltipProps> = ({
	text,
	className,
	placement = 'top',
}) => {
	const ref = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);
	const portalContainer = useFullscreenContainer();

	const checkTruncation = useCallback(() => {
		const element = ref.current;
		if (!element) return;

		const truncated =
			element.scrollWidth > element.clientWidth ||
			element.scrollHeight > element.clientHeight;
		setIsTruncated(truncated);
	}, []);

	useEffect(() => {
		checkTruncation();

		const element = ref.current;
		if (!element || typeof ResizeObserver === 'undefined') {
			window.addEventListener('resize', checkTruncation);
			return () => window.removeEventListener('resize', checkTruncation);
		}

		const observer = new ResizeObserver(checkTruncation);
		observer.observe(element);
		window.addEventListener('resize', checkTruncation);

		return () => {
			observer.disconnect();
			window.removeEventListener('resize', checkTruncation);
		};
	}, [text, checkTruncation]);

	const label = (
		<span ref={ref} className={className}>
			{text}
		</span>
	);

	if (!isTruncated) {
		return label;
	}

	return (
		<Tooltip
			title={text}
			placement={placement}
			arrow
			enterDelay={300}
			slotProps={getFullscreenPopperSlotProps(portalContainer)}
		>
			{label}
		</Tooltip>
	);
};

export default TruncatedTooltip;
